import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import httpService from '../../../services/httpService';
import Loader from '../../../shared/components/Loader';
import './InviteAck.css';

const InviteAckPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<'loading' | 'success' | 'error' | 'pending'>('loading');
  const [role, setRole] = useState<string>('');
  const [status, setStatus] = useState<string>('accepted');
  const ran = useRef(false);

  const scheduleId = searchParams.get('schedule_id') || '';
  const companyId = searchParams.get('company_id') || '';
  const email = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';
  const action = (searchParams.get('action') || 'accept').toLowerCase();

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    if (!scheduleId || !email || !token) {
      setState('pending');
      return;
    }

    const run = async () => {
      try {
        const response = await httpService.post(
          `/interview_admin/schedules/${scheduleId}/acknowledge`,
          null,
          {
            params: {
              company_id: companyId || undefined,
              email,
              token,
              status: action === 'decline' ? 'declined' : 'accepted',
            },
          }
        );
        const data = response?.data || {};
        setRole(data?.role || '');
        setStatus(data?.status || (action === 'decline' ? 'declined' : 'accepted'));
        setState('success');
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || 'Something went wrong.';
        console.error('Acknowledge failed:', msg);
        setState('error');
      }
    };
    run();
  }, [scheduleId, companyId, email, token, action]);

  const isAccepted = status === 'accepted';

  return (
    <div className="invite-ack-page">
      <Loader isLoading={state === 'loading'} message="Confirming your response..." />
      <div className="invite-ack-card">
        {state === 'pending' && (
          <>
            <div className="invite-ack-icon">ℹ️</div>
            <h1>Invalid link</h1>
            <p>The acknowledgement link you opened is missing required details. Please use the link from your email.</p>
          </>
        )}
        {state === 'error' && (
          <>
            <div className="invite-ack-icon">⚠️</div>
            <h1>Something went wrong</h1>
            <p>We could not record your response. The link may have expired. Please try again or contact the host.</p>
          </>
        )}
        {state === 'success' && (
          <>
            <div className={`invite-ack-icon ${isAccepted ? 'ok' : 'decline'}`}>
              {isAccepted ? '✓' : '✕'}
            </div>
            <h1>{isAccepted ? 'You accepted the invitation' : 'You declined the invitation'}</h1>
            <p>
              Your response as <strong>{role || 'participant'}</strong> has been recorded. The host has been notified of your availability.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default InviteAckPage;